import { useEffect, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Toolbar, CodeEditor, GraphView } from '@/components'
import { useEditorStore } from '@/store'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable'

function App() {
  const { i18n } = useTranslation()
  const { setTheme, theme } = useEditorStore()

  // Initialize theme on mount
  useEffect(() => {
    setTheme(theme)
  }, [setTheme, theme])

  // Handle embed mode via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    const embedData = params.get('data')
    const embedFormat = params.get('format')
    const embedLocale = params.get('locale')
    const embedTheme = params.get('theme')

    if (embedData) {
      try {
        const decoded = atob(embedData)
        useEditorStore.getState().setContent(decoded)
        useEditorStore.getState().parseAndRender()
      } catch {
        console.error('Failed to decode embed data')
      }
    }

    if (embedFormat) {
      useEditorStore.getState().setFormat(embedFormat as any)
    }

    if (embedLocale && ['en', 'pt', 'es'].includes(embedLocale)) {
      i18n.changeLanguage(embedLocale)
    }

    if (embedTheme && ['light', 'dark', 'system'].includes(embedTheme)) {
      setTheme(embedTheme as any)
    }

    // Post ready message for embed
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'datavision:ready' }, '*')
    }
  }, [i18n, setTheme])

  // Handle postMessage for embed communication
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'datavision:setContent') {
        const { content, format } = event.data.payload
        useEditorStore.getState().setContent(content)
        if (format) {
          useEditorStore.getState().setFormat(format)
        }
        useEditorStore.getState().parseAndRender()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="h-screen flex flex-col overflow-hidden">
        <Toolbar />

        <ResizablePanelGroup orientation="horizontal" className="flex-1">
          {/* Editor panel */}
          <ResizablePanel defaultSize={30} minSize={10}>
            <CodeEditor />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Graph panel */}
          <ResizablePanel defaultSize={70} minSize={10}>
            <GraphView />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </Suspense>
  )
}

function LoadingScreen() {
  return (
    <div className="h-screen flex items-center justify-center bg-surface-light dark:bg-surface-dark">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center animate-pulse-soft">
          <svg className="w-6 h-6 text-primary-600" viewBox="0 0 32 32" fill="currentColor">
            <path d="M8 12h4v4H8v-4zM14 8h4v4h-4V8zM20 12h4v4h-4v-4zM14 16h4v4h-4v-4z" />
          </svg>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading AIQIA DataVision...</p>
      </div>
    </div>
  )
}

export default App
